import moment from 'moment';
import { isFinite } from 'lodash';

export const toLower = function toLower(value) {
  return value ? value.toString().toLowerCase() : '';
};

export const capitalize = function capitalize(value) {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
};

export const dateFormat = function dateFormat(value, fallback = '') {
  if (!value) {
    return fallback;
  }

  return moment(value).format('DD.MM.YYYY');
};

export const dateFormatBackend = function dateFormat(value, fallback = '') {
  if (!value) {
    return fallback;
  }

  return moment(value).format('YYYY-MM-DD');
};

export const yearFormat = function dateFormat(value, fallback = '') {
  if (!value) {
    return fallback;
  }

  return moment(value).format('YYYY');
};

export const dateFormatUnderscore = function dateFormat(value, fallback = '') {
  if (!value) {
    return fallback;
  }

  return moment(value).format('DD_MM_YYYY');
};

export const dateTimeFormat = function dateTimeFormat(value, fallback = '') {
  if (!value) {
    return fallback;
  }

  return moment(value).format('DD.MM.YYYY HH:mm');
};

export const timeFormat = function timeFormat(value, fallback = '') {
  if (!value) {
    return fallback;
  }

  return moment(value).format('HH:mm');
};

export const periodFormat = function dateFormat(value) {
  if (!value) {
    return '';
  }

  return moment(value)
    .locale(
      localStorage.getItem('locale') || process.env.VUE_APP_I18N_LOCALE || 'de'
    )
    .format('MMMM YYYY');
};

export const priceFormat = function priceFormat(value, digits = 2) {
  const preparedValue = parseFloat(value);

  return !isNaN(preparedValue) && isFinite(value)
    ? preparedValue.toFixed(digits).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1'")
    : value;
};

export const minutesToDuration = function minutesToDuration(value) {
  const parsedValue = parseInt(value);

  if (isNaN(parsedValue) || !isFinite(parsedValue)) {
    return value;
  }

  let hh = (parsedValue / 60) | 0;
  let mm = parsedValue % 60;

  if (hh < 0 && mm < 0) {
    mm = Math.abs(mm);
  }

  if (hh === 0) {
    return `${mm}m`;
  }

  if (mm === 0) {
    return `${hh}h`;
  }

  return `${hh}h ${mm}m`;
};

export const secondsToDuration = function secondsToDuration(value) {
  const parsedValue = parseInt(value);

  if (isNaN(parsedValue) || !isFinite(parsedValue)) {
    return value;
  }

  return minutesToDuration(parsedValue / 60);
};
const hasValue = value => value !== null && value !== undefined && value !== '';

const normalizeInput = value => {
  const stringValue = value.toString().trim();
  return {
    isNegative: stringValue.startsWith('-'),
    absoluteValue: stringValue.replace('-', '')
  };
};

const formatIntegerWithSeparators = integerPart =>
  integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, "'");

const normalizeDecimalPart = (decimalPart = '') => {
  if (decimalPart.length === 1) return `${decimalPart}0`;
  if (decimalPart.length === 0) return null;
  return decimalPart;
};

export const formatNumberByApostrophe = (value, fallback = '') => {
  if (!hasValue(value)) return fallback;

  const { isNegative, absoluteValue } = normalizeInput(value);
  const [integerPart, decimalPart] = absoluteValue.split('.');

  const formattedInteger = formatIntegerWithSeparators(integerPart || '');
  const normalizedDecimal = normalizeDecimalPart(decimalPart);

  return [
    isNegative ? '-' : '',
    formattedInteger,
    normalizedDecimal ? `.${normalizedDecimal}` : ''
  ].join('');
};

const sanitizeNumericString = value =>
  value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');

const processNumericParts = numericString => {
  const [integerPart, ...decimalParts] = numericString.split('.');
  return {
    integer: integerPart || '0',
    decimal: decimalParts.join('').slice(0, 20)
  };
};

export const clearNumber = (value, fallback = '') => {
  if (!hasValue(value)) return fallback;

  const strValue = value.toString().trim();

  if (strValue === '-' || strValue === '0-') return '-';

  const isNegative = strValue.startsWith('-');
  const numericString = sanitizeNumericString(strValue);

  if (!numericString) return isNegative ? '-' : 0;

  const { integer, decimal } = processNumericParts(numericString);

  const numberValue = decimal
    ? parseFloat(`${integer}.${decimal}`)
    : parseInt(integer, 10);

  if (!Number.isFinite(numberValue)) return fallback;

  const result = decimal
    ? Number(numberValue.toFixed(decimal.length))
    : numberValue;

  return isNegative && result !== 0 ? `-${result}` : result.toString();
};
